import React, {useEffect, useMemo, useState} from 'react';
import { useAdmitRootStyles } from "../admitStyles";
import { useTranslation } from "react-i18next";
import {useDispatch, useSelector} from 'react-redux';
import { mergeQueuePatient } from "../../../store/reducers/patient";
import _, {filter, isArray, isEmpty} from 'lodash';
import AgyleBlockSwitch from "../../../components/ui/switch/agyleBlockSwitch";
import { FormLabel } from "@mui/material";
import AgyleDialog from "../../../components/modal/agyleDialog";
import AgyleButton from "../../../components/ui/buttons/agyleButton";
import Grid from "@mui/material/Grid";
import InformationBox from "../../../components/other/informationBox";
import AppointmentsListDialog from '../admitExtRefresh/components/appointmentsListDialog';
import useOpenState from '../../../hooks/useOpenState';
import {setSnackbar} from '../../../store/reducers/globals';
import useCheckLockPatient from '../../../hooks/ws/useCheckLockPatient';
import {getConfig} from '../../../util/configUtil';
import {appointmentsModalHelperState} from '../../../store/reducers/appointments';
import { SLOT_STATUSES } from "../../appointments/utils/constants/appointmentSessionConstants";
import API from '../../../app/api';
import moment from 'moment/moment';

export const ChooseRegistrationStep = (props) => {
  const dispatch = useDispatch();
  const classes = useAdmitRootStyles();
  const { t } = useTranslation();

  // Selected Appointment in case of going from scheduler
  const schedulerHelperState = useSelector(appointmentsModalHelperState);

  // Temp pt merge
  const patientToMerge = useSelector(mergeQueuePatient);
  const isSamePatientToMerge =
    patientToMerge?.patient?.__id === props.patient.current?.__id;
  const [optional, setOptional] = useState(false);

  // Check if the merged patient is current locked
  const [isPatientLockout] = useCheckLockPatient(patientToMerge?.patient?.__id);

  const [patientHasBookedAppointmentToday, setPatientHasBookedAppointmentToday] = useState(false);

  const [isAppointmentsListOpen, openAppointmentsList, closeAppointmentsList] = useOpenState(false, true);

  const isPatient = props.patient.current !== null;
  const isBedas =
    props.patient.current?.tags &&
    props.patient.current?.tags.includes("bedas");
  const haveActiveAtt = props.patient.current?.activeAttendances
    ? !_.isEmpty(
        _.filter(
          props.patient.current?.activeAttendances,
          (pt) => pt?.attendanceType === "E" && _.isNil(pt?.endDate),
        ),
      )
    : false;
  const isDeceased =
    props.patient?.current &&
    (props.patient?.current?.tags?.includes("deceased") ||
      !_.isNull(props.patient?.current?.dod));
  const isTemporary =
    props.patient?.current?.tags?.includes("temporary") && haveActiveAtt;
  const ptLocation = haveActiveAtt
    ? _.find(props.patientsFacilities, { id: props.patient?.current?.__id })
    : null;
  const checkHospital = getConfig('modules.map.customMap', false)
    ? _.isNull(props.hospital)
    : false;
  let wrongLocation = false;
  if (ptLocation) {
    wrongLocation = ptLocation?.location !== props.hospital?.hospitalId;
  }

  const handleOpenAppointmentsList = () => {
    if (!props.hospital) {
      dispatch(setSnackbar({ type: 'error', message: 'Please select hospital.'}))
    } else {
      openAppointmentsList();
    }
  }

  const handleMergeTemporary = (removeBedas) => {
    props.mergeTemporary(isSamePatientToMerge, removeBedas);
  };

  const disableMergeButton = () => {
    let activeAtt = false;
    if (patientToMerge && !isSamePatientToMerge) {
      activeAtt = haveActiveAtt;
    }
    return activeAtt || checkHospital;
  };
  const showOptional = () => {
    return patientToMerge && !wrongLocation ? (
      <div className={classes.divRow} style={{ marginRight: "auto" }}>
        <AgyleBlockSwitch onChange={setOptional} value={optional} />

        <FormLabel style={{ marginLeft: 5, marginRight: 5 }}>
          {t("Show optional")}
        </FormLabel>
      </div>
    ) : null;
  };

  const followUpButtonDisabled = useMemo(() => {
    return (
        haveActiveAtt ||
        checkHospital ||
        isDeceased ||
        (patientToMerge && !optional) ||
        wrongLocation
    );
  }, [haveActiveAtt, checkHospital, isDeceased, patientToMerge, optional, wrongLocation])

  const onFollowUpClick = () => {
    if (getConfig('modules.scheduler.enabled', false)) {
      if (schedulerHelperState?.__id && props.patient.current) {
        props.onAppointment(props.patient.current, schedulerHelperState)
      } else {
        handleOpenAppointmentsList()
      }
    } else {
      props.onBedas()
    }
  }

  useEffect(() => {
    if (
        isPatient && (
            getConfig('modules.bedas.enabled', false) ||
            getConfig('modules.scheduler.enabled', false)
        ) &&
        !followUpButtonDisabled &&
        schedulerHelperState?.__id &&
        props.patient.current
    ) {
      onFollowUpClick();
    }
  }, [onFollowUpClick, isPatient, followUpButtonDisabled, schedulerHelperState?.__id, props.patient.current])

  useEffect(() => {
    const fetchData = async (ptId, hospitalId) => {
      try {
        const filterStr = `patientId=${ptId}${hospitalId ? `&hospitalId=${hospitalId}` : ""}`;

        const apiResponse = await API.getAppointmentSlots(
            moment().startOf('day'),
            moment().add(1, 'day').startOf('day'),
            false,
            filterStr,
        );

        if (isArray(apiResponse)) {
          const filtered = filter(apiResponse, (slot) => {
            return (
                slot.patientId === props.patient.current?.hospitalNumber &&
                slot.status === SLOT_STATUSES.NOT_REGISTERED.value &&
                moment(slot.startDate).isSame(moment(), 'day') &&
                !!slot.patient
            );
          });

          setPatientHasBookedAppointmentToday(!isEmpty(filtered));
        }
      } catch (err) {
        dispatch(setSnackbar({type: 'error', message: "Error getting today's patient's appointments."}))
      }
    };

    if (props.patient.current?.hospitalNumber && !followUpButtonDisabled) {
      const hospitalId = props.hospital?.__id || props.hospital?.hospitalId || '';

      fetchData(props.patient.current?.hospitalNumber, hospitalId);
    }
  }, [props.patient.current?.hospitalNumber, props.hospital, followUpButtonDisabled]);

  return (
    <AgyleDialog
      title={t("Choose Registration")}
      onClose={() => {
        props.patient.current = null;
        props.onClose();
      }}
      fullWidth
      open
      darkContentBackground
      disableActionsCloseButton
      closeButtonOnHeader
      alignActionsToLeft
      disableSave
      buttons={[
        showOptional(),

        <AgyleButton
          onClick={() => {
            props.patient.current = null;
            props.onClose();
          }}
          color="primary"
        >
          Discard
        </AgyleButton>,
      ]}
    >
      <Grid container spacing={2} justifyContent="space-between">
        {checkHospital && (
          <Grid item xs={12}>
            <InformationBox type="warning">
              {t("Please choose a hospital.")}
            </InformationBox>
          </Grid>
        )}

        {wrongLocation && (
          <Grid item xs={12}>
            <InformationBox type="warning">
              {t("Patients have an open attendance at another hospital site.")}
            </InformationBox>
          </Grid>
        )}

        {patientHasBookedAppointmentToday && (
          <Grid item xs={12}>
            <InformationBox type="warning">
              {t("You are about to create a new attendance.")}
              <br/>
              {t("Please be aware this patient already has an existing planned attendance scheduler (Follow Up)")}
            </InformationBox>
          </Grid>
        )}

        {isPatientLockout && (
          <Grid item xs={12}>
            <InformationBox type="warning">
              {t("For safety reasons, the merge process for this patient is unavailable at this time.")}
              <br/>
              {t("This is due to another user actively accessing the patient record. To proceed with a merge, ensure no other users are accessing this patients record, and try again.")}
            </InformationBox>
          </Grid>
        )}

        {!isDeceased && (
          <Grid item xs={getConfig('admitPatient.bedBooking', false) ? 6 : 12}>
            <AgyleButton
              onClick={props.onCreateNewPatient}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={
                checkHospital ||
                isTemporary ||
                (patientToMerge && !optional) ||
                wrongLocation
              }
            >
              {t(
                isPatient
                  ? `${haveActiveAtt ? "Update" : "Create"} Attendance`
                  : "Create New Patient / Not in Database",
              )}
            </AgyleButton>
          </Grid>
        )}

        {isDeceased && (
          <Grid item xs={getConfig('admitPatient.bedBooking', false) ? 6 : 12}>
            <AgyleButton
              onClick={props.onUpdateDemographic}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={checkHospital || wrongLocation}
            >
              {t("Update Patient Demographic")}
            </AgyleButton>
          </Grid>
        )}

        {getConfig('admitPatient.bedBooking', false) && (
          <Grid item xs={6}>
            <AgyleButton
              onClick={props.onAddBedBooking}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={
                haveActiveAtt ||
                checkHospital ||
                isDeceased ||
                (patientToMerge && !optional) ||
                wrongLocation
              }
            >
              {t("Add To Bed Booking")}
            </AgyleButton>
          </Grid>
        )}

        {props.patient?.current && getConfig('admitPatient.reAttendanceEnabled', false) && (
            <Grid item xs={6}>
              <AgyleButton
                onClick={props.onReAttendance}
                color="primary"
                variant="contained"
                width={"100%"}
                disabled={
                  haveActiveAtt ||
                  checkHospital ||
                  isDeceased ||
                  (patientToMerge && !optional) ||
                  wrongLocation
                }
              >
                {t("Re Attendance")}
              </AgyleButton>
            </Grid>
          )}

        {isPatient && (getConfig('modules.bedas.enabled', false) || getConfig('modules.scheduler.enabled', false)) && isBedas && (
          <Grid item xs={6}>
            <AgyleButton
              onClick={onFollowUpClick}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={followUpButtonDisabled}
            >
              {t("Follow Up")}
            </AgyleButton>
          </Grid>
        )}

        {isPatient && !patientToMerge && (
          <Grid item xs={6}>
            <AgyleButton
              onClick={props.mergInTo}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={checkHospital || wrongLocation}
            >
              {t("Merge with temporary patient")}
            </AgyleButton>
          </Grid>
        )}

        {isPatient && patientToMerge && (
          <Grid item xs={6}>
            <AgyleButton
              onClick={handleMergeTemporary.bind(this, false)}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={disableMergeButton() || wrongLocation || isPatientLockout}
            >
              {t(
                `${isSamePatientToMerge ? "Create New Patient / Not in Database" : "Merge with selected temp. patient"}${isBedas ? " (BEDAS Attendance)" : ""}`,
              )}
            </AgyleButton>
          </Grid>
        )}

        {isPatient && patientToMerge && isBedas && !isSamePatientToMerge && (
          <Grid item xs={6}>
            <AgyleButton
              onClick={handleMergeTemporary.bind(this, true)}
              color="primary"
              variant="contained"
              width={"100%"}
              disabled={disableMergeButton() || wrongLocation || isPatientLockout}
            >
              {t("Merge with selected temp. patient. (New Attendance)")}
            </AgyleButton>
          </Grid>
        )}
      </Grid>

      <AppointmentsListDialog
          hospital={props.hospital}
          open={isAppointmentsListOpen}
          onClose={closeAppointmentsList}
          patient={props.patient.current}
          onSelect={props.onAppointment}
      />
    </AgyleDialog>
  );
};
